<script setup lang="ts">
    import { FriendlyCaptchaSDK, type WidgetHandle } from '@friendlycaptcha/sdk';
    import type { FrontendFieldDefinition } from '@hmmh/storyblok-forms/client';
    import type { FormInputs } from '~/utils/formUtils';
    import log from '~/utils/log';
    import { useFormFieldId } from './form-field-utils';

    const props = defineProps<{ field: FrontendFieldDefinition }>();
    const inputId = useFormFieldId();
    const formInputs = inject<Ref<FormInputs>>('formInputs')!;
    const captchaElement = useTemplateRef<HTMLDivElement>('captchaElement');
    const config = useRuntimeConfig();
    const { lang } = useLanguage();

    let widget: WidgetHandle | undefined;
    let disposed = false;
    const validationErrors = computed(() => formInputs.value[props.field.name]?.errors ?? []);

    const setResponse = (response: string) => {
        const input = formInputs.value[props.field.name];

        if (input && !disposed) input.value = response;
    };

    watch(
        () => formInputs.value[props.field.name]?.value,
        (value, previousValue) => {
            if (previousValue && !value) widget?.reset();
        }
    );

    onMounted(() => {
        const element = captchaElement.value;
        const sitekey = config.public.friendlyCaptcha.sitekey;
        if (!element || !sitekey) {
            log.error('Friendly Captcha is not properly configured.');
            return;
        }

        try {
            widget = new FriendlyCaptchaSDK().createWidget({
                ...(config.public.friendlyCaptcha.apiEndpoint
                    ? { apiEndpoint: config.public.friendlyCaptcha.apiEndpoint }
                    : {}),
                ...(lang.value ? { language: lang.value } : {}),
                element,
                formFieldName: null,
                sitekey,
                theme: 'light'
            });

            widget.addEventListener('frc:widget.complete', (event) => setResponse(event.detail.response));
            widget.addEventListener('frc:widget.error', () => setResponse(''));
            widget.addEventListener('frc:widget.expire', () => setResponse(''));
            widget.addEventListener('frc:widget.reset', () => setResponse(''));
        } catch (error) {
            log.error('Unable to create Friendly Captcha widget.', error);
        }
    });

    onBeforeUnmount(() => {
        disposed = true;
        widget?.destroy();
    });
</script>

<template>
    <div>
        <div
            class="captcha-btn-wrapper"
            :class="{ '--has-error': validationErrors.length > 0 }"
        >
            <div ref="captchaElement"></div>
        </div>
        <FormErrors
            :id="inputId"
            :validation-errors="validationErrors"
        />
    </div>
</template>
